'use client';

import { useEffect, useRef, useState } from 'react';

type AnimatedNumberProps = {
  duration?: number;
  format?: (n: number) => React.ReactNode;
  value: number;
};

const easeOutCubic = (t: number) => {
  return 1 - Math.pow(1 - t, 3);
};

export const AnimatedNumber = ({
  duration = 150,
  format = (n) => n,
  value,
}: AnimatedNumberProps) => {
  const [displayValue, setDisplayValue] = useState<number>(value);
  const ref = useRef<number | null>(null);

  useEffect(() => {
    const start = displayValue;
    const change = value - start;
    const startTime = performance.now();

    const animate = (now: number) => {
      const elapsed = now - startTime;
      if (elapsed < duration) {
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = easeOutCubic(progress);
        setDisplayValue(Math.round(start + change * easedProgress));
        ref.current = requestAnimationFrame(animate);
      } else {
        setDisplayValue(value);
      }
    };

    ref.current = requestAnimationFrame(animate);
    return () => {
      if (ref.current) cancelAnimationFrame(ref.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, duration]);

  return format(displayValue);
};
